"use client";

import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "sonner";
import { useContestBulkActionByIds } from "@/hooks/contest/useContestBulkActionByIds";
import { useTranslation } from "@/lib/react/i18n";

type LockTargetContest = {
  id: number;
  title?: string | null;
  isLocked?: boolean;
  lockReason?: string | null;
};

type ContestLockModalProps = {
  isOpen: boolean;
  onClose: () => void;
  contestIds: number[];
  actorUuid: string;
  targets?: LockTargetContest[];
  onSuccess?: () => void | Promise<void>;
};

const ContestLockModal = ({
  isOpen,
  onClose,
  contestIds,
  actorUuid,
  targets = [],
  onSuccess,
}: ContestLockModalProps) => {
  const { t } = useTranslation();
  const { submit } = useContestBulkActionByIds();

  const [mounted, setMounted] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedCount = contestIds.length;
  const isSingleSelection = selectedCount === 1;

  const singleTarget = useMemo(() => {
    return isSingleSelection && targets.length === 1 ? targets[0] : null;
  }, [isSingleSelection, targets]);

  const isEditMode = !!singleTarget?.isLocked;

  const lockedTargetsCount = useMemo(() => {
    return targets.filter((target) => !!target.isLocked).length;
  }, [targets]);

  const hasExistingLockedTargets = lockedTargetsCount > 0;

  const shouldShowSingleEditNotice = isEditMode;

  const shouldShowBulkOverwriteNotice =
    selectedCount > 1 && hasExistingLockedTargets;

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setLoading(false);
      return;
    }

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    if (singleTarget?.isLocked && singleTarget?.lockReason) {
      setReason(singleTarget.lockReason);
    } else {
      setReason("");
    }

    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, singleTarget]);

  if (!isOpen || !mounted) return null;

  const handleSubmit = async () => {
    const trimmed = reason.trim();

    if (!trimmed) {
      toast.error(t("Please enter lock reason"));
      return;
    }

    try {
      setLoading(true);

      await submit({
        contestIds,
        action: "lock",
        actorUuid,
        lockReason: trimmed,
      });

      toast.success(isEditMode ? t("Lock reason updated") : t("Contests locked"));

      setReason("");
      await onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(t("Failed to lock"));
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/60"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        className="
          absolute top-1/2 left-1/2
          w-[92%] max-w-xl
          -translate-x-1/2 -translate-y-1/2
          overflow-hidden rounded-2xl
          border border-white/15
          bg-[#0b0b0f]
          shadow-2xl
        "
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h2 className="text-xl font-bold text-white">
              {isEditMode ? t("Edit Lock") : t("Lock")}
            </h2>

            <p className="mt-1 text-sm text-white/60">
              {isEditMode
                ? t("You can update the lock reason for this contest")
                : t("Locked contests cannot be updated by normal users")}
            </p>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            aria-label={t("Close")}
            className="cursor-pointer text-xl leading-none text-white/60 hover:text-white disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="mb-4 text-sm text-white/80">
            {t("Selected")}: {selectedCount}
          </div>

          {singleTarget?.title && (
            <div className="mb-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-[11px] text-white/45">
                {t("Target Contest")}
              </div>
              <div className="mt-1 break-words text-sm text-white/90">
                {singleTarget.title}
              </div>
            </div>
          )}

          <label className="mb-2 block text-sm text-white/80">
            {t("Please enter lock reason")}
          </label>

          <textarea
            value={reason}
            disabled={loading}
            placeholder={t("Please enter lock reason")}
            onChange={(e) => setReason(e.target.value)}
            rows={6}
            className="
              w-full resize-none rounded-xl border border-white/15
              bg-black px-4 py-3 text-white outline-none
              placeholder:text-white/35
              focus:border-amber-400/60
              focus:ring-2 focus:ring-amber-400/20
              disabled:opacity-60
            "
          />

          {shouldShowSingleEditNotice && (
            <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-[12px] leading-5 text-amber-100/85">
              {t("lock_reason_overwrite_notice")}
            </div>
          )}

          {shouldShowBulkOverwriteNotice && (
            <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-[12px] leading-5 text-amber-100/85">
              <div className="mb-1 font-semibold text-amber-100">
                {t("Notice")}
              </div>
              <div>{t("bulk_lock_reason_overwrite_notice")}</div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-white/10 px-5 py-4">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="
              rounded-xl border border-white/15
              bg-white/5 px-4 py-2 text-white
              hover:bg-white/10
              disabled:opacity-50
            "
          >
            {t("cancel")}
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="
              rounded-xl border border-amber-500/30
              bg-amber-500/20 px-4 py-2 text-amber-100
              hover:bg-amber-500/30
              disabled:opacity-50
            "
          >
            {loading ? t("Loading") : isEditMode ? t("Save") : t("Lock")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContestLockModal;